import type { DateTime } from "@dmca/utils/luxon";
import {
  Box,
  // eslint-disable-next-line no-restricted-imports
  Button,
  Dialog,
  DialogActions,
  Divider,
  Popover,
  type Theme,
  useMediaQuery,
} from "@mui/material";
import {
  LocalizationProvider,
  DateCalendar,
  MultiSectionDigitalClock,
  StaticDateTimePicker,
  StaticDatePicker,
  StaticTimePicker,
} from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { useSharedProps, type SharedDatePickerProps } from "./useSharedProps";

export function DatePickerRenderer(props: SharedDatePickerProps) {
  const {
    anchorEl,
    onClose,
    sx,
    type,
    value,
    onChange: onUserChange,
    dateFormat: _, // otherwise react complains
    ...rest
  } = useSharedProps(props);

  const open = Boolean(anchorEl);

  const handleClose = () => {
    onClose?.();
  };

  const onChange = (date: DateTime | null) => {
    onUserChange?.(date);
    if (type === "date") {
      onClose?.();
    }
  };

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      {!isMobile ? (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridAutoColumns: "max-content auto max-content",
              gridAutoRows: "max-content auto max-content",
            }}
          >
            <Box
              sx={{
                display: "flex",
                margin: "0 auto",
              }}
            >
              {(type === "date" || type === "datetime") && (
                <DateCalendar
                  value={value ?? null}
                  onChange={onChange}
                  {...rest}
                />
              )}
              {type === "datetime" && (
                <Divider orientation="vertical" flexItem />
              )}
              {(type === "time" || type === "datetime") && (
                <MultiSectionDigitalClock
                  value={value ?? null}
                  onChange={onChange}
                  sx={{
                    width: "auto",
                    "& > ul": {
                      maxHeight: "inherit",
                    },
                    border: 0,
                  }}
                />
              )}
            </Box>
            {type === "datetime" && <Divider flexItem />}
            {type !== "date" && (
              <DialogActions>
                <Button
                  onClick={handleClose}
                  size="small"
                  disableRipple
                  variant="text"
                  sx={{ py: 0.3 }}
                >
                  Ok
                </Button>
              </DialogActions>
            )}
          </Box>
        </Popover>
      ) : (
        <Dialog open={open} onClose={handleClose}>
          {type === "datetime" ? (
            <StaticDateTimePicker
              sx={sx}
              value={value ?? null}
              onChange={(date) => onChange?.(date)}
            />
          ) : type === "date" ? (
            <StaticDatePicker
              sx={sx}
              value={value ?? null}
              onChange={(date) => onChange?.(date)}
            />
          ) : (
            <StaticTimePicker
              sx={sx}
              onChange={(date) => onChange?.(date as DateTime)}
            />
          )}
        </Dialog>
      )}
    </LocalizationProvider>
  );
}
